import type { FocusEvent, KeyboardEvent, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'

import { t } from '../../../common/i18n.js'
import type { Language } from '../../../common/types/i18n.js'
import { ChevronLeft, ChevronRight } from '../icon/solar-icons.js'
import { Link } from '../link/link.js'
import type { ScrollEdges } from './tabs-scroll-utils.js'
import { revealOffset, rovingIndex, scrollEdges, stepTab } from './tabs-scroll-utils.js'
import './tabs.css'

export interface TabItem {
    key: string
    label: string
    count?: number | null
    // Cross-route tabs: the destination path (rendered as a <Link>). Omit for
    // in-page tabs — then onSelect fires and the parent persists state to the URL.
    href?: string
}

export interface TabsProps {
    items: TabItem[]
    activeKey: string
    // 'underline' (default): bottom-border active. 'segmented': filled pill.
    variant?: 'underline' | 'segmented'
    // In-page mode (items without href): called with the picked key.
    onSelect?: (key: string) => void
    // Right-aligned controls (e.g. a Disconnect button, filter/sort cluster).
    actions?: ReactNode
    className?: string
    // Pin the row to the top of the scroll container as content scrolls past.
    sticky?: boolean
    // Language for the scroll-arrow labels (underline variant).
    lang: Language
}

// How far a revealed tab is kept clear of the fade on the side it was hidden under.
const REVEAL_PAD = 56

const tabElements = (row: HTMLElement): HTMLElement[] =>
    Array.from(row.querySelectorAll<HTMLElement>('.tabs__item'))

const boxOf = (el: HTMLElement) => ({ left: el.offsetLeft, width: el.offsetWidth })

const scrollRowTo = (row: HTMLElement, left: number, instant: boolean) => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    row.scrollTo({ left, behavior: instant || reduced ? 'auto' : 'smooth' })
}

const revealTab = (row: HTMLElement, tab: HTMLElement, instant = false) => {
    const target = revealOffset(row, boxOf(tab), REVEAL_PAD)
    if (target !== null) scrollRowTo(row, target, instant)
}

// The one canonical tab/subnav component. Items with `href` render as <Link>
// (cross-route, client-side, modifier-click aware); items without render as
// <button> and call onSelect. Active styling is shared across both.
//
// The underline row scrolls horizontally when it overflows (TASK-610): a fade
// on either edge shows there is more, round arrows step one tab at a time for
// mouse/touch, and the row scrolls along with keyboard focus. Focus is roving:
// Tab lands on the active item, ← → Home End move within the row.
export const Tabs = ({
    items,
    activeKey,
    variant = 'underline',
    onSelect,
    actions,
    className,
    sticky,
    lang,
}: TabsProps) => {
    const scrollable = variant === 'underline'
    const rowRef = useRef<HTMLElement>(null)
    const [edges, setEdges] = useState<ScrollEdges>({ start: false, end: false })

    // Track which edges hide content: on scroll, on resize, and when the item
    // set changes (permissions, counts, translations all change tab widths).
    useEffect(() => {
        const row = rowRef.current
        if (!scrollable || !row) return
        const update = () => setEdges(scrollEdges(row))
        update()
        row.addEventListener('scroll', update, { passive: true })
        const observer = new ResizeObserver(update)
        observer.observe(row)
        return () => {
            row.removeEventListener('scroll', update)
            observer.disconnect()
        }
    }, [scrollable, items])

    // Keep the active tab in view — instantly on mount (a deep link into a
    // far-right tab shouldn't animate, and a background tab never would) and
    // smoothly whenever the selection changes afterwards.
    const mounted = useRef(false)
    useEffect(() => {
        const row = rowRef.current
        if (!scrollable || !row) return
        const active = tabElements(row).find((el) => el.dataset.key === activeKey)
        if (active) revealTab(row, active, !mounted.current)
        mounted.current = true
    }, [scrollable, activeKey])

    const step = (direction: 1 | -1) => {
        const row = rowRef.current
        if (!row) return
        const tabs = tabElements(row)
        const index = stepTab(row, tabs.map(boxOf), direction)
        if (index !== null) revealTab(row, tabs[index])
    }

    const handleFocus = (event: FocusEvent<HTMLElement>) => {
        const row = rowRef.current
        const target = event.target as HTMLElement
        if (row && target.classList.contains('tabs__item')) revealTab(row, target)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        const row = rowRef.current
        if (!row) return
        const tabs = tabElements(row)
        const current = tabs.indexOf(event.target as HTMLElement)
        if (current === -1) return
        const next = rovingIndex(event.key, current, tabs.length)
        if (next === null) return
        event.preventDefault()
        tabs[next].focus({ preventScroll: true })
        revealTab(row, tabs[next])
        // In-page tabs activate as focus moves (WAI-ARIA automatic activation);
        // link tabs are routes, so they wait for Enter.
        const item = items[next]
        if (!item.href) onSelect?.(item.key)
    }

    const root = ['tabs', `tabs--${variant}`, sticky && 'tabs--sticky', className]
        .filter(Boolean)
        .join(' ')
    const scroll = [
        'tabs__scroll',
        edges.start && 'tabs__scroll--start',
        edges.end && 'tabs__scroll--end',
    ]
        .filter(Boolean)
        .join(' ')

    const row = (
        <nav
            ref={rowRef}
            className="tabs__items"
            onFocus={scrollable ? handleFocus : undefined}
            onKeyDown={scrollable ? handleKeyDown : undefined}
        >
            {items.map((item) => {
                const isActive = item.key === activeKey
                const tabIndex = scrollable ? (isActive ? 0 : -1) : undefined
                const inner = (
                    <>
                        {item.label}
                        {typeof item.count === 'number' && (
                            <span className="tabs__count">{item.count}</span>
                        )}
                    </>
                )

                if (item.href) {
                    return (
                        <Link
                            key={item.key}
                            href={item.href}
                            className="tabs__item"
                            isActive={isActive}
                            tabIndex={tabIndex}
                            data-key={item.key}
                        >
                            {inner}
                        </Link>
                    )
                }

                return (
                    <button
                        key={item.key}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={isActive ? 'tabs__item is-active' : 'tabs__item'}
                        tabIndex={tabIndex}
                        data-key={item.key}
                        onClick={() => onSelect?.(item.key)}
                    >
                        {inner}
                    </button>
                )
            })}
        </nav>
    )

    return (
        <div className={root}>
            {scrollable ? (
                <div className={scroll}>
                    {row}
                    {edges.start && (
                        <button
                            type="button"
                            className="tabs__arrow tabs__arrow--start"
                            tabIndex={-1}
                            aria-label={t.common.scrollTabsLeft(lang)}
                            onClick={() => step(-1)}
                        >
                            <span className="tabs__arrow-disc">
                                <ChevronLeft size={14} />
                            </span>
                        </button>
                    )}
                    {edges.end && (
                        <button
                            type="button"
                            className="tabs__arrow tabs__arrow--end"
                            tabIndex={-1}
                            aria-label={t.common.scrollTabsRight(lang)}
                            onClick={() => step(1)}
                        >
                            <span className="tabs__arrow-disc">
                                <ChevronRight size={14} />
                            </span>
                        </button>
                    )}
                </div>
            ) : (
                row
            )}
            {actions && <div className="tabs__actions">{actions}</div>}
        </div>
    )
}
